/** SimState ⇄ compact snapshot rows. Wire-format concern, not simulation. */
import type { DiscState, PlayerState, SimState } from '../sim/types.js'
import { BASE_TUNING, emptyStats } from '../sim/state.js'
import { discTuningFrom, resolveTuning, type TuningOverlay } from '../sim/tuning.js'
import type { DiscSnap, LobbySnap, MatchSnap, PlayerSnap, SnapshotMsg } from './protocol.js'
import { PICK_DECK_S, PICK_LOCK_S, REVEAL_TICKS, SIM_HZ } from '../sim/constants.js'

export function playerToSnap(p: PlayerState): PlayerSnap {
  return {
    id: p.id, name: p.name, bot: p.isBot ? 1 : 0, abs: p.absent ? 1 : 0, team: p.team, slot: p.slot,
    x: p.pos.x, y: p.pos.y, z: p.pos.z, vx: p.vel.x, vy: p.vel.y, vz: p.vel.z,
    ax: p.aim.x, az: p.aim.z,
    st: p.state, stt: p.stateTicks, sta: p.stamina,
    rgd: p.regenDelayTicks, dcd: p.dashCooldownTicks, slk: p.shieldLockoutTicks, sag: p.shieldAgeTicks,
    tax: p.throwAim.x, taz: p.throwAim.z, ddx: p.dashDir.x, ddz: p.dashDir.z,
    held: p.discsHeld.slice(), max: p.maxDiscs,
    alive: p.alive ? 1 : 0, inv: p.invulnUntilTick, rsp: p.respawnTick,
    seq: p.lastInputSeq, pb: (p.prevButtons.throw ? 1 : 0) | (p.prevButtons.dash ? 4 : 0) | (p.prevButtons.jump ? 8 : 0),
    g: p.grounded ? 1 : 0, lt: p.landTicks,
    pu: p.powerUps, imp: p.imprints, sl: p.slots,
  }
}

export function discToSnap(d: DiscState): DiscSnap {
  return {
    id: d.id, ho: d.homeOwner, hl: d.holder, th: d.thrower, team: d.team,
    st: d.state, stt: d.stateTicks,
    x: d.pos.x, y: d.pos.y, z: d.pos.z, vx: d.vel.x, vy: d.vel.y, vz: d.vel.z,
    rl: d.rangeLeft, bc: d.bounceCount, hm: d.homing ? 1 : 0, dot: d.despawnOnTimeout ? 1 : 0, lz: d.lastZoneTeam,
  }
}

function lobbySnap(s: SimState): LobbySnap | null {
  const m = s.match
  if (m.phase !== 'LOBBY_PICK' && m.phase !== 'LOBBY_LOCK' && m.phase !== 'REVEAL') return null
  const decks: Record<string, string[]> = {}
  const locks: Record<string, string> = {}
  for (const p of s.players) {
    decks[p.id] = m.phase === 'LOBBY_PICK' ? [] : (m.lobby.decks[p.id] ?? []).slice()
    const l = m.lobby.locks[p.id] ?? null
    locks[p.id] = m.phase === 'REVEAL' ? (l ?? '') : l ? 'LOCKED' : 'CHOOSING'
  }
  const total = m.phase === 'LOBBY_PICK' ? PICK_DECK_S * SIM_HZ : m.phase === 'LOBBY_LOCK' ? PICK_LOCK_S * SIM_HZ : REVEAL_TICKS
  return { decks, locks, ticksLeft: Math.max(0, Math.round(total) - m.phaseTicks) }
}

export function matchToSnap(s: SimState): MatchSnap {
  const m = s.match
  return {
    phase: m.phase, phaseTicks: m.phaseTicks, lobby: lobbySnap(s), roundResults: structuredClone(m.roundResults), roundNumber: m.roundNumber, roundIndex: m.roundIndex,
    score: [m.score[0], m.score[1]], voidRounds: m.voidRounds, timerTicks: m.timerTicks, overtimeTicks: m.overtimeTicks,
    bounds: structuredClone(m.bounds), winner: m.winner, paused: m.paused,
  }
}

export function stateToSnapshot(s: SimState, serverTime: number): SnapshotMsg {
  return { tick: s.tick, t: serverTime, players: s.players.map(playerToSnap), discs: s.discs.map(discToSnap), match: matchToSnap(s), events: s.events.slice() }
}

/** Overwrite a PlayerState from a row. Name/stats/tuning aren't on the wire. */
export function applyPlayerSnap(p: PlayerState, r: PlayerSnap): void {
  p.team = r.team; p.slot = r.slot; p.name = r.name; p.isBot = r.bot === 1; p.absent = r.abs === 1
  p.pos.x = r.x; p.pos.y = r.y; p.pos.z = r.z
  // vel.y is not on the wire: the client re-integrates it from y (spec §1.6), and a snapshot lands
  // with y already stepped, so a grounded row is exactly at rest.
  p.vel.x = r.vx; p.vel.y = r.vy ?? 0; p.vel.z = r.vz // vy on the wire: zeroing it collapsed the predicted jump arc on every reconcile
  p.grounded = r.g === 1; p.landTicks = r.lt
  p.aim = { x: r.ax, z: r.az }
  p.state = r.st; p.stateTicks = r.stt; p.stamina = r.sta
  p.regenDelayTicks = r.rgd; p.dashCooldownTicks = r.dcd; p.shieldLockoutTicks = r.slk; p.shieldAgeTicks = r.sag
  p.throwAim = { x: r.tax, z: r.taz }; p.dashDir = { x: r.ddx, z: r.ddz }
  p.discsHeld = r.held.slice(); p.maxDiscs = r.max
  p.alive = r.alive === 1; p.invulnUntilTick = r.inv; p.respawnTick = r.rsp
  p.lastInputSeq = r.seq
  p.prevButtons = { throw: (r.pb & 1) !== 0, dash: (r.pb & 4) !== 0, jump: (r.pb & 8) !== 0 }
  p.powerUps = r.pu.slice()
  p.imprints = (r.imp ?? []).slice(); p.slots = r.sl ?? 1
}

export function snapToPlayer(r: PlayerSnap, knobs?: TuningOverlay | null): PlayerState {
  const tuning = knobs ? resolveTuning([], knobs).tuning : { ...BASE_TUNING }
  const p: PlayerState = {
    id: r.id, name: r.id, team: r.team, slot: r.slot,
    pos: { x: 0, y: 0, z: 0 }, vel: { x: 0, y: 0, z: 0 }, aim: { x: 0, z: 1 },
    state: 'IDLE', stateTicks: 0, stamina: 0, regenDelayTicks: 0, dashCooldownTicks: 0, shieldLockoutTicks: 0, shieldAgeTicks: 0,
    throwAim: { x: 0, z: 1 }, dashDir: { x: 0, z: 1 }, discsHeld: [], maxDiscs: 1, alive: true, grounded: true, landTicks: 0,
    invulnUntilTick: 0, respawnTick: 0, powerUps: [], imprints: [], slots: 1, tuning, isBot: false, absent: false,
    hurtboxes: [{ dx: 0, dz: 0, r: tuning.PLAYER_RADIUS }],
    prevButtons: { throw: false, dash: false, jump: false }, lastInputSeq: -1, roundStats: emptyStats(), matchStats: emptyStats(),
  }
  applyPlayerSnap(p, r)
  return p
}

export function applyDiscSnap(d: DiscState, r: DiscSnap): void {
  d.homeOwner = r.ho; d.holder = r.hl; d.thrower = r.th; d.team = r.team
  d.state = r.st; d.stateTicks = r.stt
  d.pos.x = r.x; d.pos.y = r.y; d.pos.z = r.z
  d.vel.x = r.vx; d.vel.y = r.vy; d.vel.z = r.vz
  d.rangeLeft = r.rl; d.bounceCount = r.bc; d.homing = r.hm === 1; d.despawnOnTimeout = r.dot === 1; d.lastZoneTeam = r.lz
}

export function snapToDisc(r: DiscSnap, tuningOf?: (playerId: string) => DiscState['tuning'] | undefined): DiscState {
  const d: DiscState = {
    id: r.id, homeOwner: r.ho, holder: r.hl, thrower: r.th, team: r.team, state: r.st, stateTicks: r.stt,
    pos: { x: 0, y: 0, z: 0 }, vel: { x: 0, y: 0, z: 0 }, rangeLeft: 0, bounceCount: 0, homing: false,
    tuning: tuningOf?.(r.th ?? r.hl ?? r.ho) ?? discTuningFrom(BASE_TUNING), despawnOnTimeout: false, lastZoneTeam: r.lz,
  }
  applyDiscSnap(d, r)
  return d
}

/** Apply a full snapshot onto a SimState in place (players/discs/match). */
export function applySnapshot(s: SimState, snap: SnapshotMsg): void {
  const seen = new Set<string>()
  for (const row of snap.players) {
    seen.add(row.id)
    const p = s.players.find((x) => x.id === row.id)
    if (p) applyPlayerSnap(p, row)
    else s.players.push(snapToPlayer(row, s.knobs))
  }
  s.players = s.players.filter((p) => seen.has(p.id)).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  const seenD = new Set<number>()
  const tuningOf = (pid: string) => { const p = s.players.find((x) => x.id === pid); return p ? discTuningFrom(p.tuning) : undefined }
  for (const row of snap.discs) {
    seenD.add(row.id)
    const d = s.discs.find((x) => x.id === row.id)
    if (d) applyDiscSnap(d, row)
    else s.discs.push(snapToDisc(row, tuningOf))
  }
  s.discs = s.discs.filter((d) => seenD.has(d.id)).sort((a, b) => a.id - b.id)
  if (s.discs.length) s.nextDiscId = Math.max(s.nextDiscId, s.discs[s.discs.length - 1].id + 1)
  const m = snap.match
  s.match.phase = m.phase; s.match.phaseTicks = m.phaseTicks; s.match.roundNumber = m.roundNumber; s.match.roundIndex = m.roundIndex
  s.match.score = [m.score[0], m.score[1]]; s.match.voidRounds = m.voidRounds; s.match.timerTicks = m.timerTicks; s.match.overtimeTicks = m.overtimeTicks
  s.match.bounds = structuredClone(m.bounds); s.match.winner = m.winner; s.match.paused = m.paused
  s.match.roundResults = structuredClone(m.roundResults)
  s.tick = snap.tick
}
